import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BACKEND_URL } from '@/lib/backendUrl';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { 
  MessageCircle, 
  QrCode, 
  RefreshCw, 
  Send, 
  User, 
  Bot, 
  Clock, 
  CheckCircle2, 
  XCircle,
  Bell,
  PhoneCall,
  Wifi,
  WifiOff,
  ArrowRight,
  Smartphone,
  Settings,
  Save,
  Sparkles,
  Store,
  Package,
  CreditCard,
  Truck,
  MessageSquare,
  Zap,
  Loader2,
  Power,
  Trash2,
  Download,
  MapPin,
  Plus,
  X as XIcon,
  Navigation
} from 'lucide-react';

const API_URL = BACKEND_URL;

export const WhatsAppPage = () => {
  const { toast } = useToast();
  const [status, setStatus] = useState({ connected: false, user: null });
  const [qrCode, setQrCode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [botStatus, setBotStatus] = useState({ available: true, error: null });
  const [notifications, setNotifications] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const qrIntervalRef = useRef(null);
  
  // Configurações do Bot IA
  const [botConfig, setBotConfig] = useState({
    enabled: true,
    business_type: 'mercado',
    business_name: '',
    greeting_message: 'Olá! Bem-vindo(a)! Como posso ajudar?',
    operating_hours: '08:00 às 22:00',
    accepts_pix: true,
    accepts_card: true,
    accepts_cash: true,
    has_delivery: false,
    delivery_fee: '5.00',
    min_delivery_order: '30.00',
    delivery_areas: '',
    // Raio de entrega
    store_latitude: null,
    store_longitude: null,
    delivery_radius_km: 0,
    delivery_fee_tiers: [],
    auto_geocode_address: true,
    show_products: true,
    take_orders: true,
    transfer_to_human: true,
    auto_create_order: true,  // Nova opção: criar pedido automaticamente
    custom_instructions: ''
  });
  const [savingConfig, setSavingConfig] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [fetchingLocation, setFetchingLocation] = useState(false);
  const [testingDelivery, setTestingDelivery] = useState(false);
  const [deliveryTestResult, setDeliveryTestResult] = useState(null);
  const [testAddress, setTestAddress] = useState('');

  // Saúde dos bots + alertas (health-check)
  const [botHealth, setBotHealth] = useState(null);
  const [botAlerts, setBotAlerts] = useState([]);
  const [dismissingAlerts, setDismissingAlerts] = useState(false);

  // Buscar status do WhatsApp
  const fetchStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/whatsapp/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setStatus(data);
      
      // Se conectado, parar de buscar QR
      if (data.connected && qrIntervalRef.current) {
        clearInterval(qrIntervalRef.current);
        qrIntervalRef.current = null;
        setQrCode(null);
      }
      
      return data;
    } catch (error) {
      console.error('Erro ao buscar status:', error);
      return { connected: false, error: error.message };
    }
  }, []);

  // Buscar configurações do Bot
  const fetchBotConfig = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/whatsapp/bot-config`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data && Object.keys(data).length > 0) {
          setBotConfig(prev => ({ ...prev, ...data }));
        }
      }
    } catch (error) {
      console.error('Erro ao buscar config do bot:', error);
    } finally {
      setLoadingConfig(false);
    }
  }, []);

  // Salvar configurações do Bot
  const handleSaveBotConfig = async () => {
    setSavingConfig(true);
    try {
      const token = localStorage.getItem('token');
      // Sanitizar payload: converter null/NaN em campos numéricos e garantir tiers válidos
      const payload = {
        ...botConfig,
        delivery_radius_km: Number(botConfig.delivery_radius_km) || 0,
        store_latitude: botConfig.store_latitude === '' || botConfig.store_latitude === null || Number.isNaN(Number(botConfig.store_latitude))
          ? null : Number(botConfig.store_latitude),
        store_longitude: botConfig.store_longitude === '' || botConfig.store_longitude === null || Number.isNaN(Number(botConfig.store_longitude))
          ? null : Number(botConfig.store_longitude),
        delivery_fee_tiers: (botConfig.delivery_fee_tiers || []).map(t => ({
          from_km: Number(t.from_km) || 0,
          to_km: Number(t.to_km) || 0,
          fee: Number(t.fee) || 0,
        })),
      };
      const response = await fetch(`${API_URL}/api/whatsapp/bot-config`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        toast({ title: 'Configurações salvas!', description: 'O bot foi atualizado com suas preferências.' });
      } else {
        const err = await response.json().catch(() => ({}));
        const detail = Array.isArray(err.detail)
          ? err.detail.map(d => `${(d.loc || []).join('.')}: ${d.msg}`).join(' | ')
          : (err.detail || `HTTP ${response.status}`);
        throw new Error(detail);
      }
    } catch (error) {
      toast({ title: 'Erro ao salvar', description: error.message || 'Não foi possível salvar as configurações', variant: 'destructive' });
    } finally {
      setSavingConfig(false);
    }
  };

  // === Delivery Radius helpers ===
  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      toast({
        title: 'Geolocalização indisponível',
        description: 'Seu navegador não suporta geolocalização. Use a opção "Buscar por endereço" ou digite lat/lng manualmente.',
        variant: 'destructive'
      });
      return;
    }
    // Contexto HTTPS é obrigatório no Chrome/Edge
    if (typeof window !== 'undefined' && window.isSecureContext === false) {
      toast({
        title: 'HTTPS necessário',
        description: 'O navegador só libera geolocalização em conexões seguras (HTTPS).',
        variant: 'destructive'
      });
      return;
    }
    setFetchingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setBotConfig(prev => ({
          ...prev,
          store_latitude: Number(pos.coords.latitude.toFixed(6)),
          store_longitude: Number(pos.coords.longitude.toFixed(6)),
        }));
        toast({ title: 'Localização capturada!', description: `Lat ${pos.coords.latitude.toFixed(4)}, Lng ${pos.coords.longitude.toFixed(4)}` });
        setFetchingLocation(false);
      },
      (err) => {
        let title = 'Não foi possível obter sua localização';
        let description = err.message || 'Erro desconhecido';
        // GeolocationPositionError codes: 1=PERMISSION_DENIED, 2=POSITION_UNAVAILABLE, 3=TIMEOUT
        if (err.code === 1) {
          title = 'Permissão negada 🔒';
          description = 'O navegador bloqueou o acesso à localização. Clique no ícone de cadeado 🔒 na barra de endereço → Permissões do site → Permitir "Localização" e tente novamente. Ou use a opção "Buscar por endereço" abaixo.';
        } else if (err.code === 2) {
          title = 'Localização indisponível';
          description = 'O Windows/Mac/Linux não conseguiu determinar sua localização (GPS/Wi-Fi desabilitados?). Use "Buscar por endereço" como alternativa.';
        } else if (err.code === 3) {
          title = 'Tempo esgotado';
          description = 'O navegador demorou muito para responder. Tente novamente ou use "Buscar por endereço".';
        }
        toast({ title, description, variant: 'destructive' });
        setFetchingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  // Busca lat/lng a partir de endereço/CEP via backend (Nominatim)
  const handleLocateByAddress = async () => {
    const q = (testAddress || '').trim();
    if (!q) {
      toast({ title: 'Digite um endereço/CEP', description: 'Preencha o campo "Testar endereço" e clique neste botão.', variant: 'destructive' });
      return;
    }
    setFetchingLocation(true);
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`${API_URL}/api/whatsapp/geocode-address`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ address: q })
      });
      const data = await resp.json();
      if (!resp.ok || !data.lat || !data.lng) {
        throw new Error(data.detail || 'Endereço não localizado');
      }
      setBotConfig(prev => ({
        ...prev,
        store_latitude: Number(Number(data.lat).toFixed(6)),
        store_longitude: Number(Number(data.lng).toFixed(6)),
      }));
      toast({ title: 'Localização preenchida!', description: data.display_name || `${data.lat}, ${data.lng}` });
    } catch (err) {
      toast({ title: 'Não foi possível localizar', description: err.message, variant: 'destructive' });
    } finally {
      setFetchingLocation(false);
    }
  };

  const addDeliveryTier = () => {
    setBotConfig(prev => ({
      ...prev,
      delivery_fee_tiers: [...(prev.delivery_fee_tiers || []), { from_km: 0, to_km: 3, fee: 5 }]
    }));
  };

  const updateDeliveryTier = (idx, field, value) => {
    setBotConfig(prev => {
      const tiers = [...(prev.delivery_fee_tiers || [])];
      tiers[idx] = { ...tiers[idx], [field]: parseFloat(value) || 0 };
      return { ...prev, delivery_fee_tiers: tiers };
    });
  };

  const removeDeliveryTier = (idx) => {
    setBotConfig(prev => ({
      ...prev,
      delivery_fee_tiers: (prev.delivery_fee_tiers || []).filter((_, i) => i !== idx)
    }));
  };

  const testDeliveryAddress = async () => {
    if (!testAddress.trim()) {
      toast({ title: 'Digite um endereço', variant: 'destructive' });
      return;
    }
    if (!botConfig.store_latitude || !botConfig.store_longitude || !botConfig.delivery_radius_km) {
      toast({ title: 'Configure a loja primeiro', description: 'Defina a localização e o raio antes de testar.', variant: 'destructive' });
      return;
    }
    setTestingDelivery(true);
    setDeliveryTestResult(null);
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`${API_URL}/api/whatsapp/check-delivery`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ address: testAddress })
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Falha ao consultar');
      setDeliveryTestResult(data);
    } catch (err) {
      toast({ title: 'Erro', description: err.message, variant: 'destructive' });
    } finally {
      setTestingDelivery(false);
    }
  };

  // Buscar QR Code
  const fetchQrCode = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/whatsapp/qr`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      // Verificar se o serviço está disponível
      if (data.error) {
        setBotStatus({ available: false, error: data.error });
        setQrCode(null);
        return data;
      }
      
      setBotStatus({ available: true, error: null });
      
      if (data.qrBase64) {
        setQrCode(data.qrBase64);
      } else if (data.qr) {
        setQrCode(data.qr);
      } else if (data.connected) {
        setQrCode(null);
        if (qrIntervalRef.current) {
          clearInterval(qrIntervalRef.current);
          qrIntervalRef.current = null;
        }
      }
      
      return data;
    } catch (error) {
      console.error('Erro ao buscar QR:', error);
      setBotStatus({ available: false, error: 'Serviço não disponível' });
      return { qr: null, error: error.message };
    }
  }, []);

  // Buscar notificações
  const fetchNotifications = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/whatsapp/notifications`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setNotifications(data.notifications || []);
    } catch (error) {
      console.error('Erro ao buscar notificações:', error);
    }
  }, []);

  // Saúde dos bots (health-check)
  const fetchBotHealth = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const [healthRes, alertsRes] = await Promise.all([
        fetch(`${API_URL}/api/whatsapp/health`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/whatsapp/health/alerts`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      if (healthRes.ok) setBotHealth(await healthRes.json());
      if (alertsRes.ok) {
        const d = await alertsRes.json();
        setBotAlerts(d.alerts || []);
      }
    } catch (error) {
      console.error('Erro ao buscar saúde dos bots:', error);
    }
  }, []);

  const dismissAllBotAlerts = useCallback(async () => {
    setDismissingAlerts(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/whatsapp/health/alerts/dismiss-all`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setBotAlerts([]);
        toast({ title: 'Alertas dispensados', description: 'Lista de alertas limpa.' });
      }
    } catch (error) {
      toast({ title: 'Erro ao dispensar alertas', description: error.message, variant: 'destructive' });
    } finally {
      setDismissingAlerts(false);
    }
  }, [toast]);

  // Iniciar polling de QR Code
  const startQrPolling = useCallback(() => {
    if (qrIntervalRef.current) {
      clearInterval(qrIntervalRef.current);
    }
    
    fetchQrCode();
    qrIntervalRef.current = setInterval(async () => {
      const result = await fetchQrCode();
      if (result.connected) {
        clearInterval(qrIntervalRef.current);
        qrIntervalRef.current = null;
        await fetchStatus();
        toast({ title: 'WhatsApp conectado!', description: 'Bot pronto para uso.' });
      }
    }, 3000);
  }, [fetchQrCode, fetchStatus, toast]);

  // Efeito inicial
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      const statusData = await fetchStatus();
      await fetchNotifications();
      await fetchBotConfig();
      await fetchBotHealth();
      
      if (!statusData.connected) {
        startQrPolling();
      }
      
      setLoading(false);
    };
    
    init();
    
    // Polling de notificações a cada 10 segundos
    const notifInterval = setInterval(fetchNotifications, 10000);
    // Polling de saúde dos bots a cada 30 segundos
    const healthInterval = setInterval(fetchBotHealth, 30000);
    
    return () => {
      if (qrIntervalRef.current) clearInterval(qrIntervalRef.current);
      clearInterval(notifInterval);
      clearInterval(healthInterval);
    };
  }, [fetchStatus, fetchNotifications, fetchBotConfig, fetchBotHealth, startQrPolling]);

  // Enviar resposta
  const handleSendReply = async () => {
    if (!selectedChat || !replyMessage.trim()) return;
    
    setSendingReply(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/whatsapp/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          phone_number: selectedChat.customer_phone,
          message: replyMessage
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({ title: 'Mensagem enviada!', description: `Para ${selectedChat.customer_phone}` });
        setReplyMessage('');
        
        // Marcar notificação como lida
        await fetch(`${API_URL}/api/whatsapp/notifications/${selectedChat.id}/read`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        await fetchNotifications();
      } else {
        toast({ title: 'Erro ao enviar', description: data.error || 'Tente novamente', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Erro', description: error.message, variant: 'destructive' });
    } finally {
      setSendingReply(false);
    }
  };

  // Reconectar
  const handleReconnect = () => {
    setQrCode(null);
    startQrPolling();
    toast({ title: 'Reconectando...', description: 'Aguarde o QR Code' });
  };

  // Limpar sessão completamente
  const handleClearSession = async () => {
    setLoggingOut(true);
    try {
      const token = localStorage.getItem('token');
      toast({ title: 'Limpando sessão...', description: 'Aguarde alguns segundos' });
      
      const response = await fetch(`${API_URL}/api/whatsapp/clear-session`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success) {
        toast({ 
          title: 'Sessão limpa!', 
          description: 'Aguarde o novo QR Code' 
        });
        setStatus({ connected: false, user: null });
        setQrCode(null);
        
        // Aguardar e iniciar polling
        setTimeout(() => {
          startQrPolling();
        }, 5000);
      } else {
        toast({ 
          title: 'Erro ao limpar sessão', 
          description: data.error || 'Tente novamente', 
          variant: 'destructive' 
        });
      }
    } catch (error) {
      toast({ 
        title: 'Erro', 
        description: error.message, 
        variant: 'destructive' 
      });
    } finally {
      setLoggingOut(false);
    }
  };

  // Desconectar e gerar novo QR Code
  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/whatsapp/logout`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success) {
        toast({ 
          title: 'Sessão encerrada', 
          description: 'Aguarde alguns segundos para o novo QR Code' 
        });
        setStatus({ connected: false, user: null });
        setQrCode(null);
        
        // Aguardar um pouco e iniciar polling
        setTimeout(() => {
          startQrPolling();
        }, 3000);
      } else {
        toast({ 
          title: 'Erro ao desconectar', 
          description: data.error || 'Tente novamente', 
          variant: 'destructive' 
        });
      }
    } catch (error) {
      toast({ 
        title: 'Erro', 
        description: error.message, 
        variant: 'destructive' 
      });
    } finally {
      setLoggingOut(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-emerald-600" />
          <p className="text-slate-600">Carregando WhatsApp Bot...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="whatsapp-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <MessageCircle className="w-8 h-8 text-emerald-600" />
            WhatsApp Bot
          </h1>
          <p className="text-slate-500 mt-1">Atendimento automatizado inteligente</p>
        </div>
        
        <div className="flex items-center gap-3">
          <Badge 
            variant={status.connected ? "default" : "secondary"}
            className={status.connected ? "bg-emerald-500" : "bg-slate-400"}
            data-testid="connection-status"
          >
            {status.connected ? (
              <><Wifi className="w-3 h-3 mr-1" /> Conectado</>
            ) : (
              <><WifiOff className="w-3 h-3 mr-1" /> Desconectado</>
            )}
          </Badge>
          
          {notifications.length > 0 && (
            <Badge variant="destructive" data-testid="notification-count">
              <Bell className="w-3 h-3 mr-1" />
              {notifications.length} pendente{notifications.length > 1 ? 's' : ''}
            </Badge>
          )}
        </div>
      </div>

      {/* Banner de saúde dos bots — só mostra para o bot principal */}
      {(botAlerts.length > 0 || (botHealth?.bots?.whatsapp_bot && !botHealth.bots.whatsapp_bot.reachable)) && (
        <div 
          className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4"
          data-testid="bot-health-banner"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 rounded-full bg-amber-200 p-1.5">
                <WifiOff className="h-4 w-4 text-amber-700" />
              </div>
              <div>
                <div className="font-semibold text-amber-900">
                  Alerta de saúde dos bots
                </div>
                <div className="text-sm text-amber-800 mt-1 space-y-1">
                  {botHealth && Object.entries(botHealth.bots || {})
                    .filter(([name]) => name === 'whatsapp_bot')
                    .map(([name, info]) => (
                    <div key={name} className="flex items-center gap-2" data-testid={`bot-health-${name}`}>
                      <span className={`inline-block w-2 h-2 rounded-full ${info.connected ? 'bg-emerald-500' : info.reachable ? 'bg-amber-500' : 'bg-red-500'}`} />
                      <span className="font-medium">Bot WhatsApp:</span>
                      <span>
                        {info.connected ? 'Conectado' : info.reachable ? 'Online, sem sessão (aguardando QR)' : 'Serviço fora do ar'}
                      </span>
                    </div>
                  ))}
                  {botAlerts.length > 0 && (
                    <div className="mt-2 text-amber-800">
                      <strong>{botAlerts.length}</strong> alerta{botAlerts.length > 1 ? 's' : ''} pendente{botAlerts.length > 1 ? 's' : ''} de quedas recentes.
                    </div>
                  )}
                </div>
              </div>
            </div>
            {botAlerts.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={dismissAllBotAlerts}
                disabled={dismissingAlerts}
                data-testid="dismiss-bot-alerts-btn"
              >
                {dismissingAlerts ? (
                  <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Dispensando...</>
                ) : (
                  <>Dispensar</>
                )}
              </Button>
            )}
          </div>
        </div>
      )}

      <Tabs defaultValue={status.connected ? "config" : "conexao"} className="space-y-6">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="conexao" className="data-[state=active]:bg-white">
            <QrCode className="w-4 h-4 mr-2" />
            Conexão
          </TabsTrigger>
          <TabsTrigger value="config" className="data-[state=active]:bg-white">
            <Settings className="w-4 h-4 mr-2" />
            Configurar Bot IA
          </TabsTrigger>
          <TabsTrigger value="atendimentos" className="data-[state=active]:bg-white">
            <PhoneCall className="w-4 h-4 mr-2" />
            Atendimentos
            {notifications.length > 0 && (
              <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                {notifications.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="instrucoes" className="data-[state=active]:bg-white">
            <Bot className="w-4 h-4 mr-2" />
            Como Funciona
          </TabsTrigger>
        </TabsList>

        {/* Aba Conexão */}
        <TabsContent value="conexao">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Status Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Smartphone className="w-5 h-5" />
                  Status da Conexão
                </CardTitle>
                <CardDescription>
                  Informações sobre a conexão com WhatsApp
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <span className="text-slate-600">Status</span>
                  <div className="flex items-center gap-2">
                    {status.connected ? (
                      <>
                        <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                        <span className="font-medium text-emerald-600">Conectado</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="w-5 h-5 text-red-500" />
                        <span className="font-medium text-red-600">Desconectado</span>
                      </>
                    )}
                  </div>
                </div>
                
                {status.user && (
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <span className="text-slate-600">Número</span>
                    <span className="font-mono font-medium">{status.user.id?.split(':')[0] || 'N/A'}</span>
                  </div>
                )}
                
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <span className="text-slate-600">Sessões Humanas</span>
                  <span className="font-medium">{status.humanSessions || 0}</span>
                </div>
                
                {!status.connected && (
                  <Button onClick={handleReconnect} className="w-full" variant="outline">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Reconectar
                  </Button>
                )}
                
                {status.connected && (
                  <Button 
                    onClick={handleLogout} 
                    className="w-full" 
                    variant="destructive"
                    disabled={loggingOut}
                  >
                    {loggingOut ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <WifiOff className="w-4 h-4 mr-2" />
                    )}
                    Desconectar e Gerar Novo QR
                  </Button>
                )}
                
                {/* Botão para limpar sessão - aparece quando não está conectado */}
                {!status.connected && (
                  <Button 
                    onClick={handleClearSession} 
                    className="w-full mt-2" 
                    variant="outline"
                    disabled={loggingOut}
                  >
                    {loggingOut ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4 mr-2" />
                    )}
                    Limpar Sessão e Reiniciar
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* QR Code Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <QrCode className="w-5 h-5" />
                  QR Code
                </CardTitle>
                <CardDescription>
                  Escaneie com seu WhatsApp para conectar
                </CardDescription>
              </CardHeader>
              <CardContent>
                {status.connected ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <CheckCircle2 className="w-16 h-16 text-emerald-500 mb-4" />
                    <p className="text-lg font-medium text-emerald-600">WhatsApp Conectado!</p>
                    <p className="text-sm text-slate-500 mt-2">O bot está pronto para receber mensagens</p>
                  </div>
                ) : qrCode ? (
                  <div className="flex flex-col items-center">
                    <div className="p-4 bg-white border-2 border-slate-200 rounded-lg" data-testid="qr-code-display">
                      {/* QR Code como imagem base64 */}
                      {qrCode.startsWith('data:') ? (
                        <img src={qrCode} alt="QR Code WhatsApp" className="w-64 h-64" />
                      ) : (
                        <pre className="font-mono text-[6px] leading-[6px] text-black whitespace-pre">
                          {qrCode}
                        </pre>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 mt-4 text-center">
                      Abra o WhatsApp no celular<br />
                      Vá em <strong>Configurações</strong> → <strong>Aparelhos conectados</strong><br />
                      Toque em <strong>Conectar aparelho</strong>
                    </p>
                    <Button onClick={() => fetchQrCode()} variant="ghost" className="mt-4">
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Atualizar QR Code
                    </Button>
                  </div>
                ) : botStatus.error ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <WifiOff className="w-12 h-12 text-red-400 mb-4" />
                    <p className="text-slate-700 font-medium">Sessão do WhatsApp encerrada</p>
                    <p className="text-sm text-slate-600 mt-2 max-w-md">
                      {botStatus.error}
                    </p>
                    <p className="text-xs text-slate-400 mt-4 max-w-md">
                      Isso pode acontecer quando o WhatsApp remove o dispositivo (aparelhos conectados)
                      ou por inatividade. Clique em <strong>Resetar Sessão</strong> abaixo para gerar um novo QR Code.
                    </p>
                    <div className="flex gap-2 mt-4">
                      <Button onClick={() => fetchQrCode()} variant="outline" data-testid="btn-retry-qr">
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Tentar Novamente
                      </Button>
                      <Button
                        onClick={async () => {
                          try {
                            const token = localStorage.getItem('token');
                            const r = await fetch(`${API_URL}/api/whatsapp/reset`, {
                              method: 'POST',
                              headers: { 'Authorization': `Bearer ${token}` },
                            });
                            const d = await r.json();
                            if (d.success) {
                              toast({ title: 'Sessão resetada', description: 'Aguarde alguns segundos e clique em Atualizar QR' });
                              setBotStatus({ available: true, error: null });
                              setTimeout(() => fetchQrCode(), 4000);
                            } else {
                              toast({ title: 'Erro', description: d.error || 'Falha ao resetar', variant: 'destructive' });
                            }
                          } catch (e) {
                            toast({ title: 'Erro', description: e.message, variant: 'destructive' });
                          }
                        }}
                        variant="default"
                        className="bg-emerald-600 hover:bg-emerald-700"
                        data-testid="btn-reset-session"
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Resetar Sessão
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <RefreshCw className="w-12 h-12 text-slate-400 animate-spin mb-4" />
                    <p className="text-slate-500">Gerando QR Code...</p>
                    <p className="text-sm text-slate-400 mt-2">Aguarde alguns segundos</p>
                    <Button onClick={() => fetchQrCode()} variant="ghost" className="mt-4">
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Atualizar
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Aba Configuração do Bot IA */}
        <TabsContent value="config">
          <div className="space-y-6">
            {/* Header com destaque */}
            <Card className="bg-gradient-to-r from-violet-500 to-purple-600 text-white border-0">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-white/20 rounded-xl">
                    <Sparkles className="w-8 h-8" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">Bot com Inteligência Artificial</h2>
                    <p className="text-violet-100">Configure como o bot deve atender seus clientes automaticamente</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="grid gap-6 md:grid-cols-2">
              {/* Informações do Negócio */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Store className="w-5 h-5 text-violet-500" />
                    Informações do Negócio
                  </CardTitle>
                  <CardDescription>Dados que o bot vai usar para responder os clientes</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Tipo de Negócio</Label>
                    <select
                      value={botConfig.business_type}
                      onChange={(e) => setBotConfig({...botConfig, business_type: e.target.value})}
                      className="w-full h-10 px-3 border border-slate-300 rounded-lg mt-1"
                    >
                      <option value="mercado">Mercado / Mercadinho</option>
                      <option value="adega">Adega / Distribuidora</option>
                      <option value="lanchonete">Lanchonete / Fast Food</option>
                      <option value="restaurante">Restaurante</option>
                      <option value="padaria">Padaria / Confeitaria</option>
                      <option value="acougue">Açougue / Frigorífico</option>
                      <option value="petshop">Pet Shop</option>
                      <option value="farmacia">Farmácia</option>
                      <option value="loja">Loja / Comércio Geral</option>
                      <option value="servicos">Prestação de Serviços</option>
                    </select>
                  </div>
                  
                  <div>
                    <Label>Nome do Estabelecimento</Label>
                    <Input
                      value={botConfig.business_name}
                      onChange={(e) => setBotConfig({...botConfig, business_name: e.target.value})}
                      placeholder="Ex: Mercadinho do João"
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label>Horário de Funcionamento</Label>
                    <Input
                      value={botConfig.operating_hours}
                      onChange={(e) => setBotConfig({...botConfig, operating_hours: e.target.value})}
                      placeholder="Ex: Segunda a Sábado, 08:00 às 22:00"
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label>Mensagem de Boas-vindas</Label>
                    <textarea
                      value={botConfig.greeting_message}
                      onChange={(e) => setBotConfig({...botConfig, greeting_message: e.target.value})}
                      placeholder="Olá! Bem-vindo(a)! Como posso ajudar?"
                      className="w-full h-20 px-3 py-2 border border-slate-300 rounded-lg mt-1 resize-none"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Formas de Pagamento */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-emerald-500" />
                    Formas de Pagamento
                  </CardTitle>
                  <CardDescription>Informe quais pagamentos você aceita</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                        <span className="text-lg">💵</span>
                      </div>
                      <span className="font-medium">PIX</span>
                    </div>
                    <Switch
                      checked={botConfig.accepts_pix}
                      onCheckedChange={(checked) => setBotConfig({...botConfig, accepts_pix: checked})}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-lg">💳</span>
                      </div>
                      <span className="font-medium">Cartão (Débito/Crédito)</span>
                    </div>
                    <Switch
                      checked={botConfig.accepts_card}
                      onCheckedChange={(checked) => setBotConfig({...botConfig, accepts_card: checked})}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                        <span className="text-lg">💰</span>
                      </div>
                      <span className="font-medium">Dinheiro</span>
                    </div>
                    <Switch
                      checked={botConfig.accepts_cash}
                      onCheckedChange={(checked) => setBotConfig({...botConfig, accepts_cash: checked})}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Delivery */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Truck className="w-5 h-5 text-amber-500" />
                    Delivery / Entrega
                  </CardTitle>
                  <CardDescription>Configure se oferece entrega</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="font-medium">Faz Delivery?</span>
                    <Switch
                      checked={botConfig.has_delivery}
                      onCheckedChange={(checked) => setBotConfig({...botConfig, has_delivery: checked})}
                    />
                  </div>
                  
                  {botConfig.has_delivery && (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Taxa de Entrega (R$)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={botConfig.delivery_fee}
                            onChange={(e) => setBotConfig({...botConfig, delivery_fee: e.target.value})}
                            placeholder="5.00"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label>Pedido Mínimo (R$)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={botConfig.min_delivery_order}
                            onChange={(e) => setBotConfig({...botConfig, min_delivery_order: e.target.value})}
                            placeholder="30.00"
                            className="mt-1"
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Bairros/Áreas de Entrega</Label>
                        <Input
                          value={botConfig.delivery_areas}
                          onChange={(e) => setBotConfig({...botConfig, delivery_areas: e.target.value})}
                          placeholder="Ex: Centro, Jardim, Vila Nova..."
                          className="mt-1"
                        />
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Raio de Entrega (Localização da Loja) */}
              {botConfig.has_delivery && (
                <Card data-testid="delivery-radius-card">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-emerald-600" />
                      Raio de Entrega & Taxa por Distância
                    </CardTitle>
                    <CardDescription>
                      Defina a localização da loja. O bot pede a localização do cliente e só aceita pedidos dentro do raio.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label>Latitude da loja</Label>
                        <Input
                          type="number"
                          step="0.000001"
                          data-testid="store-latitude-input"
                          value={botConfig.store_latitude ?? ''}
                          onChange={(e) => setBotConfig({ ...botConfig, store_latitude: e.target.value === '' ? null : parseFloat(e.target.value) })}
                          placeholder="-23.55052"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Longitude da loja</Label>
                        <Input
                          type="number"
                          step="0.000001"
                          data-testid="store-longitude-input"
                          value={botConfig.store_longitude ?? ''}
                          onChange={(e) => setBotConfig({ ...botConfig, store_longitude: e.target.value === '' ? null : parseFloat(e.target.value) })}
                          placeholder="-46.63331"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Raio máximo (km)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          min="0"
                          data-testid="delivery-radius-input"
                          value={botConfig.delivery_radius_km ?? 0}
                          onChange={(e) => setBotConfig({ ...botConfig, delivery_radius_km: parseFloat(e.target.value) || 0 })}
                          placeholder="5"
                          className="mt-1"
                        />
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleUseMyLocation}
                        disabled={fetchingLocation}
                        data-testid="use-my-location-btn"
                      >
                        {fetchingLocation ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Navigation className="w-4 h-4 mr-2" />}
                        Usar minha localização atual
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleLocateByAddress}
                        disabled={fetchingLocation}
                        data-testid="locate-by-address-btn"
                        title="Preenche lat/lng pelo endereço/CEP digitado no campo 'Testar endereço' abaixo"
                      >
                        {fetchingLocation ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MapPin className="w-4 h-4 mr-2" />}
                        Buscar por endereço/CEP
                      </Button>
                      {botConfig.store_latitude && botConfig.store_longitude && (
                        <a
                          href={`https://www.google.com/maps?q=${botConfig.store_latitude},${botConfig.store_longitude}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center px-3 py-1.5 text-sm rounded-md border border-slate-200 hover:bg-slate-50"
                          data-testid="view-on-map-link"
                        >
                          <MapPin className="w-4 h-4 mr-2" />
                          Ver no mapa
                        </a>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 -mt-2">
                      💡 Se o botão &quot;Usar minha localização&quot; não funcionar, verifique se o navegador concedeu permissão (ícone 🔒 na barra de endereço) ou use &quot;Buscar por endereço/CEP&quot; com o endereço preenchido no campo abaixo.
                    </p>

                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <span className="font-medium text-sm">Geocodificar endereço digitado</span>
                        <p className="text-xs text-slate-500">Se o cliente digitar um endereço em texto, o bot tentará localizá-lo automaticamente.</p>
                      </div>
                      <Switch
                        data-testid="auto-geocode-switch"
                        checked={botConfig.auto_geocode_address}
                        onCheckedChange={(checked) => setBotConfig({ ...botConfig, auto_geocode_address: checked })}
                      />
                    </div>

                    {/* Faixas de taxa por distância */}
                    <div className="border rounded-lg p-3 bg-white">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <Label className="text-sm font-semibold">Taxas por faixa de distância</Label>
                          <p className="text-xs text-slate-500">Se vazio, usa a &quot;Taxa de Entrega&quot; fixa acima.</p>
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={addDeliveryTier}
                          data-testid="add-delivery-tier-btn"
                        >
                          <Plus className="w-4 h-4 mr-1" /> Adicionar faixa
                        </Button>
                      </div>

                      {(botConfig.delivery_fee_tiers || []).length === 0 ? (
                        <p className="text-sm text-slate-400 italic">Nenhuma faixa configurada.</p>
                      ) : (
                        <div className="space-y-2">
                          {(botConfig.delivery_fee_tiers || []).map((tier, idx) => (
                            <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`delivery-tier-row-${idx}`}>
                              <div className="col-span-3">
                                <Label className="text-xs">De (km)</Label>
                                <Input
                                  type="number"
                                  step="0.1"
                                  min="0"
                                  value={tier.from_km}
                                  onChange={(e) => updateDeliveryTier(idx, 'from_km', e.target.value)}
                                />
                              </div>
                              <div className="col-span-3">
                                <Label className="text-xs">Até (km)</Label>
                                <Input
                                  type="number"
                                  step="0.1"
                                  min="0"
                                  value={tier.to_km}
                                  onChange={(e) => updateDeliveryTier(idx, 'to_km', e.target.value)}
                                />
                              </div>
                              <div className="col-span-5">
                                <Label className="text-xs">Taxa (R$)</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={tier.fee}
                                  onChange={(e) => updateDeliveryTier(idx, 'fee', e.target.value)}
                                />
                              </div>
                              <div className="col-span-1 flex items-end justify-end h-full">
                                <Button
                                  type="button"
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => removeDeliveryTier(idx)}
                                  data-testid={`remove-delivery-tier-${idx}`}
                                  className="text-red-500 hover:text-red-600"
                                >
                                  <XIcon className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Testar endereço */}
                    <div className="border border-dashed rounded-lg p-3 bg-slate-50">
                      <Label className="text-sm font-semibold">Testar endereço</Label>
                      <p className="text-xs text-slate-500 mb-2">Digite um endereço para conferir se está dentro do raio configurado.</p>
                      <div className="flex gap-2">
                        <Input
                          placeholder="Ex: Rua Paulista, 1000, São Paulo"
                          value={testAddress}
                          onChange={(e) => setTestAddress(e.target.value)}
                          data-testid="test-delivery-address-input"
                        />
                        <Button
                          type="button"
                          onClick={testDeliveryAddress}
                          disabled={testingDelivery}
                          data-testid="test-delivery-btn"
                        >
                          {testingDelivery ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Testar'}
                        </Button>
                      </div>
                      {deliveryTestResult && (
                        <div
                          className={`mt-3 p-3 rounded-md text-sm ${deliveryTestResult.within_radius ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-red-50 text-red-800 border border-red-200'}`}
                          data-testid="delivery-test-result"
                        >
                          {deliveryTestResult.geocoded === false ? (
                            <>❌ Não foi possível localizar o endereço.</>
                          ) : (
                            <>
                              <div><strong>Distância:</strong> {deliveryTestResult.distance_km?.toFixed(2)} km</div>
                              <div><strong>Dentro do raio:</strong> {deliveryTestResult.within_radius ? 'Sim ✅' : 'Não ❌'}</div>
                              {deliveryTestResult.within_radius && (
                                <div><strong>Taxa calculada:</strong> R$ {Number(deliveryTestResult.fee || 0).toFixed(2)}</div>
                              )}
                              {deliveryTestResult.resolved_address && (
                                <div className="mt-1 text-xs opacity-80">{deliveryTestResult.resolved_address}</div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Comportamento do Bot */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-blue-500" />
                    Comportamento do Bot
                  </CardTitle>
                  <CardDescription>O que o bot deve fazer automaticamente</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <span className="font-medium">Mostrar Produtos e Preços</span>
                      <p className="text-sm text-slate-500">Bot informa preços quando cliente perguntar</p>
                    </div>
                    <Switch
                      checked={botConfig.show_products}
                      onCheckedChange={(checked) => setBotConfig({...botConfig, show_products: checked})}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <span className="font-medium">Receber Pedidos</span>
                      <p className="text-sm text-slate-500">Bot anota pedidos do cliente</p>
                    </div>
                    <Switch
                      checked={botConfig.take_orders}
                      onCheckedChange={(checked) => setBotConfig({...botConfig, take_orders: checked})}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <span className="font-medium">Transferir para Humano</span>
                      <p className="text-sm text-slate-500">Permite cliente falar com atendente</p>
                    </div>
                    <Switch
                      checked={botConfig.transfer_to_human}
                      onCheckedChange={(checked) => setBotConfig({...botConfig, transfer_to_human: checked})}
                    />
                  </div>
                  
                  {/* Nova opção: Criar pedido automaticamente */}
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                    <div>
                      <span className="font-medium text-green-700">Criar Pedido Automático</span>
                      <p className="text-sm text-green-600">Bot finaliza e insere pedido no sistema</p>
                    </div>
                    <Switch
                      checked={botConfig.auto_create_order}
                      onCheckedChange={(checked) => setBotConfig({...botConfig, auto_create_order: checked})}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Instruções Personalizadas */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-yellow-500" />
                  Instruções Adicionais (Opcional)
                </CardTitle>
                <CardDescription>Informações extras que o bot deve saber sobre seu negócio</CardDescription>
              </CardHeader>
              <CardContent>
                <textarea
                  value={botConfig.custom_instructions}
                  onChange={(e) => setBotConfig({...botConfig, custom_instructions: e.target.value})}
                  placeholder="Ex: Temos promoção de cerveja toda sexta-feira. Não vendemos fiado. Aceitamos encomendas para festas..."
                  className="w-full h-24 px-3 py-2 border border-slate-300 rounded-lg resize-none"
                />
                <p className="text-sm text-slate-500 mt-2">
                  O bot usará essas informações para responder de forma mais personalizada.
                </p>
              </CardContent>
            </Card>

            {/* Botão Salvar */}
            <div className="flex justify-end">
              <Button 
                onClick={handleSaveBotConfig}
                disabled={savingConfig}
                className="bg-violet-600 hover:bg-violet-700 px-8"
              >
                {savingConfig ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Salvando...</>
                ) : (
                  <><Save className="w-4 h-4 mr-2" /> Salvar Configurações</>
                )}
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* Aba Atendimentos */}
        <TabsContent value="atendimentos">
          <div className="grid gap-6 md:grid-cols-3">
            {/* Lista de Notificações */}
            <Card className="md:col-span-1">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="w-5 h-5" />
                  Atendimentos Pendentes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  {notifications.length === 0 ? (
                    <div className="text-center py-8 text-slate-400">
                      <CheckCircle2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Nenhum atendimento pendente</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {notifications.map((notif) => (
                        <button
                          key={notif.id}
                          onClick={() => setSelectedChat(notif)}
                          className={`w-full p-3 rounded-lg text-left transition-colors ${
                            selectedChat?.id === notif.id 
                              ? 'bg-emerald-50 border-2 border-emerald-500' 
                              : 'bg-slate-50 hover:bg-slate-100 border-2 border-transparent'
                          }`}
                          data-testid={`notification-${notif.id}`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <User className="w-4 h-4 text-slate-400" />
                            <span className="font-medium text-sm">{notif.customer_phone}</span>
                          </div>
                          <p className="text-xs text-slate-500 line-clamp-2">{notif.message}</p>
                          <div className="flex items-center gap-1 mt-2 text-xs text-slate-400">
                            <Clock className="w-3 h-3" />
                            {new Date(notif.created_at).toLocaleString('pt-BR')}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Chat/Resposta */}
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5" />
                  {selectedChat ? `Chat com ${selectedChat.customer_phone}` : 'Selecione um atendimento'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {selectedChat ? (
                  <div className="space-y-4">
                    {/* Mensagem do cliente */}
                    <div className="bg-slate-100 p-4 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <User className="w-4 h-4 text-slate-500" />
                        <span className="font-medium text-sm">{selectedChat.customer_phone}</span>
                        <span className="text-xs text-slate-400">
                          {new Date(selectedChat.created_at).toLocaleString('pt-BR')}
                        </span>
                      </div>
                      <p className="text-slate-700 whitespace-pre-wrap">{selectedChat.message}</p>
                    </div>

                    {/* Campo de resposta */}
                    <div className="space-y-3">
                      <label className="text-sm font-medium text-slate-700">Sua resposta:</label>
                      <textarea
                        value={replyMessage}
                        onChange={(e) => setReplyMessage(e.target.value)}
                        placeholder="Digite sua resposta para o cliente..."
                        className="w-full h-32 p-3 border rounded-lg resize-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        data-testid="reply-input"
                      />
                      <div className="flex gap-2">
                        <Button 
                          onClick={handleSendReply} 
                          disabled={!replyMessage.trim() || sendingReply || !status.connected}
                          className="bg-emerald-600 hover:bg-emerald-700"
                          data-testid="send-reply-button"
                        >
                          {sendingReply ? (
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Send className="w-4 h-4 mr-2" />
                          )}
                          Enviar Resposta
                        </Button>
                        
                        {!status.connected && (
                          <p className="text-sm text-red-500 flex items-center">
                            <XCircle className="w-4 h-4 mr-1" />
                            WhatsApp desconectado
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 text-center text-slate-400">
                    <MessageCircle className="w-16 h-16 mb-4 opacity-50" />
                    <p className="text-lg">Selecione um atendimento</p>
                    <p className="text-sm">Clique em uma notificação à esquerda para responder</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Aba Instruções */}
        <TabsContent value="instrucoes">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bot className="w-5 h-5" />
                Como Funciona o Bot
              </CardTitle>
              <CardDescription>
                Entenda o funcionamento do atendimento automatizado
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg flex items-center gap-2">
                    <span className="w-8 h-8 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center text-sm font-bold">1</span>
                    Cliente Envia Mensagem
                  </h3>
                  <p className="text-slate-600 ml-10">
                    Quando um cliente envia mensagem para seu WhatsApp, o bot recebe e processa automaticamente.
                  </p>

                  <h3 className="font-semibold text-lg flex items-center gap-2">
                    <span className="w-8 h-8 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center text-sm font-bold">2</span>
                    Resposta Automática Inteligente
                  </h3>
                  <p className="text-slate-600 ml-10">
                    O sistema analisa a mensagem e responde automaticamente como um vendedor, oferecendo produtos, preços e informações da loja.
                  </p>

                  <h3 className="font-semibold text-lg flex items-center gap-2">
                    <span className="w-8 h-8 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center text-sm font-bold">3</span>
                    Transferência para Humano
                  </h3>
                  <p className="text-slate-600 ml-10">
                    Se o cliente digitar <strong>&quot;9&quot;</strong> ou pedir atendente, a conversa é transferida para você responder manualmente.
                  </p>
                </div>

                <div className="bg-slate-50 p-6 rounded-lg">
                  <h3 className="font-semibold text-lg mb-4">Comandos do Cliente</h3>
                  
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-3 bg-white rounded-lg">
                      <span className="w-10 h-10 bg-emerald-500 text-white rounded-full flex items-center justify-center font-bold">9</span>
                      <div>
                        <p className="font-medium">Falar com Atendente</p>
                        <p className="text-sm text-slate-500">Transfere para atendimento humano</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 p-3 bg-white rounded-lg">
                      <span className="w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold">0</span>
                      <div>
                        <p className="font-medium">Voltar ao Bot</p>
                        <p className="text-sm text-slate-500">Retorna ao atendimento automático</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-800">
                      <strong>Importante:</strong> Configure seus produtos e informações da loja em <strong>Configurações → Catálogo Online</strong> para o bot ter informações corretas.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>
    </div>
  );
};
